import Chart from 'react-apexcharts'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { useCallback, useEffect, useState } from 'react'
import { twinFetchPostJSON } from '../../../../utils/globals/data'
import { getISOCurrencyMain } from '../../../../utils/reducers/getters'
import { preciseDecimalSum } from '@teinor/erp/prices'
import { getCurrencySymbol } from '../../../../utils/globals/price'
import { SellInvoiceMultipleChartData } from './types'
import useDictMonths from '../../../../utils/hooks/useDictMonths'
import { TwinDictionary } from '../../../../utils/globals/dictionary'
import { displayFormatedNumber } from '../../../../utils/globals/numbers'
import { SellInvoiceStatus, sellInvoiceStatus } from '@teinor/erp/types/company/order/sell/sellInvoice'
import './sellinvoicemultiplechartdata.sass'

interface DSBSellInvoiceMultipleChartDataProps extends DSBSellInvoiceMultipleChartDataLogic {
    title?: string
}

const DSBSellInvoiceMultipleChartData = ({title, end_date, start_date }: DSBSellInvoiceMultipleChartDataProps) => {
    const { t } = useTwinTranslation()
    const { sellInvoices } = useDSBSellInvoiceMultipleChartDataLogic({end_date, start_date})
    const mainCurrency = getISOCurrencyMain()
    const currencySymbol = getCurrencySymbol(mainCurrency)
    const { arrMonthShort } = useDictMonths()
    const customlegendFormatter = (name: any, opts: any) => {
        return `
            <div class='legend-label'>${name}</div>
            <div class='legend-value'>${displayFormatedNumber(opts.w.globals.seriesTotals[opts.seriesIndex].toFixed(2))}${currencySymbol}</div>
        `
    }
    const series = [
        {
            name: t('creating', 'Borrador'),
            type: 'column',
            data: sellInvoices.creating
        },
        {
            name: t('accepted', 'Aceptados'),
            type: 'column',
            data: sellInvoices.accepted
        },
        {
            name: t('cancelleds', 'Cancelados'),
            type: 'column',
            data: sellInvoices.refunded
        },
        {
            name: t('total', 'Total'),
            type: 'area',
            data: sellInvoices.totals
        }
    ]
    return (
        <div className='flex flex-col gap-10 flex-auto h-1 sell_invoice_multiplechart_data'>
            <h3>{title}</h3>
            <div className='flex-auto h-1 flex flex-col'>
                <div className='flex-auto'>
                    <Chart options={{
                        chart: {
                            type: 'line',
                            stacked: false,
                            events: {
                                mounted: (chart) => {
                                    chart.windowResizeHandler()
                                }
                            },

                            toolbar: {
                                show: false
                            },
                            zoom: {
                                enabled: true
                            }
                        },
                        plotOptions: {
                            bar: {
                                horizontal: false,
                                columnWidth: '60%',
                            },
                        },
                        stroke: {
                            show: true,
                            curve: 'smooth',
                            width: 2,
                        },
                        yaxis: {
                            labels: {
                                style: {
                                    colors: '#8F9BA4',
                                    fontFamily: 'Roboto'
                                }
                            },
                        },
                        xaxis: {
                            labels: {
                                style: {
                                    fontFamily: 'Roboto',
                                    colors: '#8F9BA4',
                                }
                            },
                            categories: arrMonthShort
                        },
                        fill: {
                            opacity: 1,
                        },
                        legend: {
                            position: 'top',
                            horizontalAlign: 'left',
                            formatter: customlegendFormatter,
                            fontFamily: 'Roboto',
                            markers: {
                                offsetY: 3
                            }
                        },
                        colors: ['#8F9BA4', '#1DB27B', '#EB2D53', '#DBF1FF'],
                        tooltip: {
                            y: {
                                formatter: function (val) {
                                    return displayFormatedNumber(val.toFixed(2)) + ' ' + currencySymbol
                                }
                            }
                        }
                    }} series={series} type="line" height={'100%'} />
                </div>
            </div>
        </div>
    )
}

interface DSBSellInvoiceMultipleChartDataLogic {
    start_date: Date
    end_date: Date
}

const useDSBSellInvoiceMultipleChartDataLogic = ({end_date, start_date}: DSBSellInvoiceMultipleChartDataLogic) => {
    const [sellInvoices, setSellInvoices] = useState<SellInvoiceMultipleChartData>({ creating: [], accepted: [], acceptedTotal: 0, refunded: [], refundedTotal: 0, totals: [], totalTotal: 0 })

    const getData = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/order/sell/sellInvoice/getAllSellInvoicesForStats', {
            attributes: ['total_without_vat', 'status', 'createdAt'],
            where: { 'SellInvoice.createdAt': { '$betweenN': [start_date, end_date] }, is_last: true }
        })
        const mySellInvoiceStatus: TwinDictionary = { creating: Array(12).fill(0), creatingTotal: 0, accepted: Array(12).fill(0), acceptedTotal: 0, refunded: Array(12).fill(0), refundedTotal: 0, totals: Array(12).fill(0), totalTotal: 0 }


        if (result) {
            for (const element of result) {
                const date = new Date(element.createdAt)
                const month = date.getMonth()
                let myStatusInversed = sellInvoiceStatus[element.status as keyof SellInvoiceStatus]
                if (Array.isArray(mySellInvoiceStatus[myStatusInversed])) {
                    const myTotal = parseFloat(element.total_without_vat)
                    mySellInvoiceStatus[myStatusInversed][month] = parseFloat(preciseDecimalSum(myTotal, mySellInvoiceStatus[myStatusInversed][month]).toFixed(2))
                    mySellInvoiceStatus['totals'][month] = parseFloat(preciseDecimalSum(myTotal, mySellInvoiceStatus['totals'][month]).toFixed(2))
                    mySellInvoiceStatus[myStatusInversed + 'Total'] = parseFloat(preciseDecimalSum(myTotal, mySellInvoiceStatus[myStatusInversed + 'Total']).toFixed(2))
                    mySellInvoiceStatus['totalTotal'] = parseFloat(preciseDecimalSum(myTotal, mySellInvoiceStatus['totalTotal']).toFixed(2))
                }
            } 
            setSellInvoices(mySellInvoiceStatus as SellInvoiceMultipleChartData )
        }
    }, [setSellInvoices, end_date, start_date])


    useEffect(() => {
        getData()
    }, [getData])
    return { sellInvoices }
}


export default DSBSellInvoiceMultipleChartData